import { createRoute, redirect } from '@tanstack/react-router';
import { ProjectIdSchema, type ProjectId, SquadIdSchema } from '@voll-intelligence/domain';

import { queryClient, queryKeys } from '@web/core/query';
import { meRepository, projectsRepository, squadsRepository } from '@web/data/repositories';
import { SquadEditorLayout, SquadIdentityPage, SquadSettingsPage } from '@web/ui/squads/views';

import { orgWorkspaceLayoutRoute } from './overview_routes';

/**
 * Guard squad-editor routes on `kanban:projects:update` of the parent
 * project. Squads inherit the project's update permission — no separate
 * `kanban:squads:*` family exists in the backend permission table.
 *
 * Redirects silently to `/orgs/$orgSlug/overview` when the caller can't
 * edit. Project / squad fetches that 403 (Guest hitting a hand-crafted
 * URL) are treated as "no access" rather than letting the error
 * propagate to the router's generic error boundary.
 */
function redirectToOrgHome(orgSlug: string): never {
  return redirect({ throw: true, to: '/orgs/$orgSlug/overview', params: { orgSlug } }) as never;
}

async function resolveOrgIdFromSlug(orgSlug: string): Promise<string> {
  const me = await queryClient.ensureQueryData({
    queryKey: queryKeys.me.all,
    queryFn: () => meRepository.getMe(),
  });
  const org = me.orgs.find((o) => o.slug === orgSlug);
  if (!org) return redirect({ throw: true, to: '/' }) as never;
  return org.id;
}

async function requireCanEditProject(orgId: string, projectId: ProjectId): Promise<void> {
  let project;
  try {
    project = await queryClient.ensureQueryData({
      queryKey: queryKeys.projects.detail(orgId, projectId),
      queryFn: () => projectsRepository.getProject(orgId, projectId),
    });
  } catch {
    project = null;
  }
  if (!project?.permissions.includes('kanban:projects:update')) {
    redirectToOrgHome('');
  }
}

async function requireCanEditFromProjectSearch(
  orgSlug: string,
  projectIdParam: string,
): Promise<void> {
  const parsedProjectId = ProjectIdSchema.safeParse(projectIdParam);
  if (!parsedProjectId.success) {
    redirectToOrgHome(orgSlug);
  }
  const orgId = await resolveOrgIdFromSlug(orgSlug);
  await requireCanEditProject(orgId, parsedProjectId.data).catch(() => redirectToOrgHome(orgSlug));
}

async function requireCanEditFromSquad(orgSlug: string, squadIdParam: string): Promise<void> {
  const parsedSquadId = SquadIdSchema.safeParse(squadIdParam);
  if (!parsedSquadId.success) {
    redirectToOrgHome(orgSlug);
  }
  const orgId = await resolveOrgIdFromSlug(orgSlug);
  let detail;
  try {
    detail = await queryClient.ensureQueryData({
      queryKey: queryKeys.squads.byId(orgId, parsedSquadId.data),
      queryFn: () => squadsRepository.getSquadById(orgId, parsedSquadId.data),
    });
  } catch {
    redirectToOrgHome(orgSlug);
  }
  await requireCanEditProject(orgId, detail.projectId).catch(() => redirectToOrgHome(orgSlug));
}

/**
 * Search-param shape for `/orgs/$orgSlug/squad/new`. The flattened
 * route drops `projectId` from the URL path, so callers (sidebar, project
 * squads page) pass it as a query string. Falls back to redirecting the
 * user to the overview when missing or malformed — squad creation needs
 * a parent project to attach to.
 */
export interface SquadNewSearch {
  projectId?: ProjectId;
}

export const squadNewLayoutRoute = createRoute({
  getParentRoute: () => orgWorkspaceLayoutRoute,
  path: 'squad/new',
  component: SquadEditorLayout,
  validateSearch: (input: Record<string, unknown>): SquadNewSearch => {
    if (typeof input.projectId !== 'string') return {};
    const parsed = ProjectIdSchema.safeParse(input.projectId);
    return parsed.success ? { projectId: parsed.data } : {};
  },
  beforeLoad: async ({ params, search }) => {
    if (!search.projectId) redirectToOrgHome(params.orgSlug);
    await requireCanEditFromProjectSearch(params.orgSlug, search.projectId);
  },
});

export const squadNewIdentityRoute = createRoute({
  getParentRoute: () => squadNewLayoutRoute,
  path: '/',
  component: SquadIdentityPage,
});

export const squadEditLayoutRoute = createRoute({
  getParentRoute: () => orgWorkspaceLayoutRoute,
  path: 'squad/$squadId/edit',
  component: SquadEditorLayout,
  beforeLoad: ({ params }) => requireCanEditFromSquad(params.orgSlug, params.squadId),
});

// Identity is the index of the squad editor — `/squad/$squadId/edit`
// renders the identity page directly, no redirect.
export const squadIdentityRoute = createRoute({
  getParentRoute: () => squadEditLayoutRoute,
  path: '/',
  component: SquadIdentityPage,
});

export const squadSettingsRoute = createRoute({
  getParentRoute: () => squadEditLayoutRoute,
  path: 'settings',
  component: SquadSettingsPage,
});
